/**
 * TreasuryPage — Module Trésorerie SuiviPlus
 * Tabs : Vue mensuelle / Flux bancaire / Vue annuelle / Budget
 * Accès admin uniquement.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, Upload, Banknote, Calendar,
  ChevronLeft, ChevronRight, Plus, Trash2, Edit, Download,
  RefreshCw, Building, Filter, X, Check, FileText, BarChart3,
  CheckCircle, Clock, Link, Unlink, AlertCircle, FileCheck, Tag,
} from 'lucide-react';
import {
  getTreasuryDashboard,
  getTreasuryDashboardSummary,
  getTreasuryConsolidated,
  getTreasuryCategories,
  getTreasuryTransactions,
  getTreasuryBudgets,
  upsertTreasuryBudget,
  createTreasuryTransaction,
  updateTreasuryTransaction,
  deleteTreasuryTransaction,
  treasuryImportPreview,
  treasuryImportConfirm,
  getTreasuryImportBatches,
  deleteTreasuryImportBatch,
  validateTreasuryTransaction,
  batchValidateTreasuryTransactions,
  batchCategorizeTreasuryTransactions,
  getTreasuryMatchingSuggestions,
  confirmTreasuryMatch,
  unmatchTreasuryTransaction,
  exportTreasuryISuite,
  getTreasuryPendingInvoices,
  uploadTreasuryInvoice,
  updateTreasuryInvoice,
  approveTreasuryInvoice,
  rejectTreasuryInvoice,
  deleteTreasuryInvoice,
  getTreasuryInvoiceMatchingTransactions,
  treasuryImportPdfPreview,
} from '../api';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useStructure } from '../contexts/StructureContext';
import { useToast } from '../contexts/NotificationContext';
import LoadingSpinner from '../components/LoadingSpinner';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(n || 0);

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
};

const MONTH_LABELS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const MONTH_SHORT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const BANK_LABELS = {
  credit_cooperatif: 'Crédit Coopératif',
  boursobank: 'Boursobank',
  unknown: 'Banque inconnue',
};

// Compat: données existantes peuvent avoir recette/depense au lieu de income/expense
const isIncome = (type) => type === 'income' || type === 'recette';

// Exercice fiscal Sep → Août : "Exercice 2025-26" quand selectedYear = 2025
const fmtExercice = (year) => `Exercice ${year}-${String(year + 1).slice(2)}`;

// Génère la séquence des 12 mois Sep → Août pour un exercice donné
const getExerciceMonths = (year) =>
  Array.from({ length: 12 }, (_, i) => {
    const monthIndex = (8 + i) % 12; // 8 = September (0-indexed)
    const rowYear = i < 4 ? year : year + 1; // Sep-Déc = year, Jan-Août = year+1
    const monthNum = monthIndex + 1;
    return {
      monthKey: `${rowYear}-${String(monthNum).padStart(2, '0')}`,
      monthLabel: MONTH_SHORT[monthIndex],
    };
  });

const inputCls =
  'border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 w-full';

// ─── KPI Card ─────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, color = 'text-gray-900', icon: Icon }) => (
  <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
    <div className="flex items-center justify-between mb-1">
      <p className="text-xs text-gray-500">{label}</p>
      {Icon && <Icon size={15} className={color} />}
    </div>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

// ─── Modal import PDF relevé bancaire (OCR LLM) ───────────────────────────────

const ImportPdfModal = ({ categories, structureId, onClose, onSuccess }) => {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [hint, setHint] = useState('');
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleAnalyze = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('structureId', structureId || 'common');
      if (hint) fd.append('hint', hint);
      const res = await treasuryImportPdfPreview(fd);
      setPreview(res.data);
      setRows((res.data.rows || []).map((r, i) => ({ ...r, _key: i, include: true })));
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur extraction PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    const toImport = rows.filter(r => r.include);
    if (!toImport.length) return;
    setLoading(true);
    try {
      await treasuryImportConfirm({
        structureId: structureId || 'common',
        bank: 'pdf_ocr',
        fileName: file.name,
        transactions: toImport.map(r => ({
          date: r.date,
          label: r.label,
          amount: r.amount,
          type: r.type === 'recette' ? 'income' : r.type === 'depense' ? 'expense' : r.type,
          categoryId: r.suggestedCategoryId || null,
          bankAccount: 'pdf_ocr',
        })),
      });
      toast.success(`${toImport.length} transaction(s) importee(s)`);
      onSuccess();
      onClose();
    } catch (e) {
      toast.error('Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileText size={18} className="text-primary" />
            {step === 1 ? 'Import PDF relevé bancaire' : `${preview?.count || 0} transactions détectées`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        {/* Étape 1 — Upload */}
        {step === 1 && (
          <div className="p-5 space-y-4">
            <p className="text-sm text-gray-600">
              Importez un relevé bancaire au format PDF. Le LLM va extraire les transactions automatiquement.
            </p>
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
              <FileText size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-500 mb-3">Relevé bancaire PDF (texte sélectionnable)</p>
              <input
                type="file"
                accept=".pdf"
                onChange={e => setFile(e.target.files[0] || null)}
                className="text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
              />
              {file && <p className="mt-2 text-xs text-green-600 font-medium">{file.name}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Indication au LLM (optionnel)
              </label>
              <input
                type="text"
                placeholder="ex: Relevé Crédit Coopératif janvier 2024"
                value={hint}
                onChange={e => setHint(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Les PDF scannés (images) ne sont pas supportés — le PDF doit avoir du texte sélectionnable.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Annuler</button>
              <button
                onClick={handleAnalyze}
                disabled={!file || loading}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
                {loading ? 'Analyse en cours...' : 'Analyser'}
              </button>
            </div>
          </div>
        )}

        {/* Étape 2 — Preview + confirmation */}
        {step === 2 && (
          <>
            <div className="flex-1 overflow-y-auto p-5">
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="px-2 py-2 text-left">
                        <input
                          type="checkbox"
                          checked={rows.every(r => r.include)}
                          onChange={e => setRows(prev => prev.map(r => ({ ...r, include: e.target.checked })))}
                        />
                      </th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">Date</th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">Libellé</th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">Catégorie</th>
                      <th className="px-3 py-2 text-right text-gray-500 font-medium">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const isRec = r.type === 'recette' || r.type === 'income';
                      return (
                        <tr key={r._key} className={`border-b border-gray-50 ${!r.include ? 'opacity-40' : ''}`}>
                          <td className="px-2 py-1.5">
                            <input
                              type="checkbox"
                              checked={r.include}
                              onChange={e => setRows(prev => prev.map((x, j) => j === i ? { ...x, include: e.target.checked } : x))}
                            />
                          </td>
                          <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap">{fmtDate(r.date)}</td>
                          <td className="px-3 py-1.5 text-gray-700 max-w-[180px] truncate" title={r.label}>{r.label}</td>
                          <td className="px-3 py-1.5">
                            <select
                              className="border border-gray-200 rounded px-1.5 py-0.5 text-xs text-gray-700 bg-white"
                              value={r.suggestedCategoryId || ''}
                              onChange={e => setRows(prev => prev.map((x, j) => j === i ? { ...x, suggestedCategoryId: e.target.value || null } : x))}
                            >
                              <option value="">— Sans catégorie</option>
                              {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className={`px-3 py-1.5 text-right font-medium ${isRec ? 'text-green-600' : 'text-red-600'}`}>
                            {isRec ? '+' : '-'}{fmt(Math.abs(r.amount))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-between items-center flex-shrink-0">
              <button onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-gray-700">← Retour</button>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Annuler</button>
                <button
                  onClick={handleConfirm}
                  disabled={loading || rows.filter(r => r.include).length === 0}
                  className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                  Importer {rows.filter(r => r.include).length} transaction(s)
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Modal import CSV (Flux bancaire) ─────────────────────────────────────────

const ImportCsvModal = ({ categories, structureId, onClose, onSuccess }) => {
  const toast = useToast();
  const [step, setStep] = useState(1); // 1 = upload, 2 = preview + confirm
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { bank, rows: [{date, label, amount, type, suggestedCategoryId}] }
  const [rows, setRows] = useState([]); // rows with editable categoryId
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setPreview(null);
    setError(null);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (structureId) fd.append('structureId', structureId);
      const res = await treasuryImportPreview(fd);
      setPreview(res.data);
      setRows(
        (res.data.rows || []).map((r) => ({ ...r, categoryId: r.suggestedCategoryId ? String(r.suggestedCategoryId) : '' }))
      );
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.error || "Erreur lors de l'analyse du fichier");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      await treasuryImportConfirm({
        structureId,
        bank: preview?.bank || 'credit_cooperatif',
        fileName: file?.name || 'import.csv',
        transactions: rows.map(r => ({
          date: r.date,
          label: r.label,
          amount: r.amount,
          type: r.type === 'recette' ? 'income' : r.type === 'depense' ? 'expense' : r.type,
          bankAccount: r.bankAccount,
          categoryId: r.categoryId || null,
        })),
      });
      toast.success('Import termine');
      onSuccess();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || "Erreur lors de l'import");
    } finally {
      setLoading(false);
    }
  };

  const updateRowCategory = (index, categoryId) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, categoryId } : r)));
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Upload size={18} className="text-primary" />
            Import relevé bancaire CSV
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <X size={15} className="flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {/* Étape 1 — Upload */}
          {step === 1 && (
            <>
              <p className="text-sm text-gray-600">
                Importez un relevé bancaire CSV (Crédit Coopératif ou Boursobank).
                Les transactions seront détectées automatiquement avec leur catégorie suggérée.
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fichier CSV *</label>
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                />
              </div>
              <button
                onClick={handlePreview}
                disabled={!file || loading}
                className="w-full py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 transition"
              >
                {loading ? 'Analyse en cours...' : 'Analyser le fichier →'}
              </button>
            </>
          )}

          {/* Étape 2 — Preview + confirmation */}
          {step === 2 && preview && (
            <>
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-800">
                <Building size={14} />
                Banque détectée : <strong>{BANK_LABELS[preview.bank] || preview.bank || 'Inconnue'}</strong>
                &nbsp;—&nbsp;{rows.length} transaction{rows.length > 1 ? 's' : ''} détectée{rows.length > 1 ? 's' : ''}
              </div>

              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-3 py-2 text-gray-500 font-medium">Date</th>
                      <th className="text-left px-3 py-2 text-gray-500 font-medium">Libellé</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium">Montant</th>
                      <th className="text-left px-3 py-2 text-gray-500 font-medium">Type</th>
                      <th className="text-left px-3 py-2 text-gray-500 font-medium w-40">Catégorie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap">{fmtDate(row.date)}</td>
                        <td className="px-3 py-1.5 text-gray-700 max-w-[180px] truncate" title={row.label}>
                          {row.label}
                        </td>
                        <td
                          className={`px-3 py-1.5 text-right font-medium whitespace-nowrap ${
                            row.amount >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {fmt(row.amount)}
                        </td>
                        <td className="px-3 py-1.5 text-gray-500 capitalize">{row.type}</td>
                        <td className="px-3 py-1.5">
                          <select
                            className={`border rounded px-1.5 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-primary/30 ${
                              row.categoryId ? 'border-primary/40 bg-primary/5' : 'border-gray-200'
                            }`}
                            value={row.categoryId || ''}
                            onChange={(e) => updateRowCategory(i, e.target.value)}
                          >
                            <option value="">
                              {row.suggestedCategoryName
                                ? `💡 ${row.suggestedCategoryName} (créer la catégorie)`
                                : '— Sans catégorie —'}
                            </option>
                            {(categories || []).map((c) => (
                              <option key={c.id} value={String(c.id)}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                >
                  ← Retour
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={loading || rows.length === 0}
                  className="flex-1 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 transition flex items-center justify-center gap-2"
                >
                  <Check size={15} />
                  {loading ? 'Import en cours...' : `Confirmer l'import (${rows.length})`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Modal ajout/édition transaction manuelle ─────────────────────────────────

const TransactionModal = ({ transaction, categories, structureId, onClose, onSuccess }) => {
  const toast = useToast();
  const isEdit = !!transaction;
  const [form, setForm] = useState({
    date: transaction?.date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
    label: transaction?.label || '',
    amount: transaction?.amount != null ? String(transaction.amount) : '',
    type: transaction?.type || 'expense',
    categoryId: transaction?.categoryId ? String(transaction.categoryId) : '',
    bank: transaction?.bankAccount || '',
    notes: transaction?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.label.trim() || !form.amount || !form.date) {
      setError('Renseignez la date, le libellé et le montant');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        date: form.date,
        label: form.label,
        type: form.type,
        notes: form.notes,
        bankAccount: form.bank || null,
        amount: parseFloat(form.amount),
        categoryId: form.categoryId ? parseInt(form.categoryId, 10) : null,
        structureId,
      };
      if (isEdit) {
        await updateTreasuryTransaction(transaction.id, payload);
        toast.success('Transaction modifiee');
      } else {
        await createTreasuryTransaction(payload);
        toast.success('Transaction ajoutee');
      }
      onSuccess();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">
            {isEdit ? 'Modifier la transaction' : 'Nouvelle transaction'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
              <input
                type="date"
                className={inputCls}
                value={form.date}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => handleChange('date', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type *</label>
              <select
                className={inputCls}
                value={form.type}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <option value="income">Recette</option>
                <option value="expense">Dépense</option>
                <option value="virement">Virement</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Libellé *</label>
            <input
              type="text"
              className={inputCls}
              placeholder="Description de la transaction"
              value={form.label}
              onChange={(e) => handleChange('label', e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Montant (€) *</label>
              <input
                type="number"
                step="0.01"
                className={inputCls}
                placeholder="0.00"
                value={form.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Banque</label>
              <input
                type="text"
                className={inputCls}
                placeholder="ex: Crédit Coopératif"
                value={form.bank}
                onChange={(e) => handleChange('bank', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Catégorie</label>
            <select
              className={inputCls}
              value={form.categoryId}
              onChange={(e) => handleChange('categoryId', e.target.value)}
            >
              <option value="">— Sans catégorie —</option>
              {(categories || []).map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea
              className={inputCls}
              rows={2}
              placeholder="Notes optionnelles"
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 transition"
            >
              {loading ? 'Enregistrement...' : isEdit ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Onglet Tableau de bord (graphiques Recharts) ─────────────────────────────

const PIE_COLORS = ['#10b981', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

const CustomTooltipArea = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name} : {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(p.value)}
        </p>
      ))}
    </div>
  );
};

const DashboardTab = ({ structureId, year }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!structureId) return;
    setLoading(true);
    getTreasuryDashboardSummary({ structureId, year })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [structureId, year]);

  if (loading) return <div className="py-10"><LoadingSpinner size="large" text="Chargement du tableau de bord..." /></div>;
  if (!data) return null;

  const { kpis, months, pieData } = data;
  const fmtK = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard label="Recettes" value={fmtK(kpis.totalRecettes)} color="text-green-600" icon={TrendingUp} />
        <KpiCard label="Depenses" value={fmtK(kpis.totalDepenses)} color="text-red-500" icon={TrendingDown} />
        <KpiCard
          label="Solde"
          value={fmtK(kpis.balance)}
          color={kpis.balance >= 0 ? 'text-primary' : 'text-red-600'}
          icon={Banknote}
          sub={kpis.balance >= 0 ? 'Excedent' : 'Deficit'}
        />
        <KpiCard
          label="A valider"
          value={kpis.toValidate}
          color={kpis.toValidate > 0 ? 'text-amber-600' : 'text-gray-500'}
          sub="transactions"
        />
      </div>

      {/* Graphique évolution 12 mois */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Evolution Recettes / Depenses — {year}</h3>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={months} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="gradRecettes" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradDepenses" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={v => `${Math.round(v / 1000)}k`} />
            <Tooltip content={<CustomTooltipArea />} />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="recettes" name="Recettes" stroke="#10b981" strokeWidth={2} fill="url(#gradRecettes)" dot={false} />
            <Area type="monotone" dataKey="depenses" name="Depenses" stroke="#ef4444" strokeWidth={2} fill="url(#gradDepenses)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* PieChart + table top catégories */}
      {pieData?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Repartition des depenses par categorie</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                  {pieData.map((entry, index) => (
                    <Cell key={index} fill={entry.color || PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtK(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Top depenses</h3>
            <div className="space-y-2">
              {pieData.map((item, i) => {
                const pct = kpis.totalDepenses > 0 ? Math.round((item.value / kpis.totalDepenses) * 100) : 0;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color || PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-sm text-gray-700 flex-1 truncate">{item.name}</span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{pct}%</span>
                    <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">{fmtK(item.value)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Onglet Vue mensuelle ─────────────────────────────────────────────────────

const MonthlyTab = ({ structureId, month, year }) => {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!structureId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getTreasuryDashboard({ structureId, month, year, view: 'monthly' });
      setData(res.data);
    } catch (e) {
      setError('Erreur de chargement des données mensuelles');
      console.error(e);
      toast.error('Erreur de chargement des donnees mensuelles');
    } finally {
      setLoading(false);
    }
  }, [structureId, month, year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <div className="py-10"><LoadingSpinner size="large" text="Chargement..." /></div>;
  if (error)
    return (
      <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
        {error}
      </div>
    );
  if (!data || !data.categories?.length)
    return (
      <div className="text-center py-16 text-gray-400">
        <BarChart3 size={40} className="mx-auto mb-3 opacity-30" />
        <p className="font-medium">Aucune donnée pour ce mois</p>
        <p className="text-sm mt-1">Ajoutez des transactions ou saisissez un budget pour commencer.</p>
      </div>
    );

  const totalBudget = data.categories.reduce((s, c) => s + (c.budget || 0), 0);
  const totalRealise = data.categories.reduce((s, c) => s + (c.realise || 0), 0);
  const totalEcart = totalRealise - totalBudget;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <KpiCard
          label="Budget total"
          value={fmt(totalBudget)}
          color="text-primary"
          icon={BarChart3}
        />
        <KpiCard
          label="Réalisé"
          value={fmt(totalRealise)}
          color={totalRealise >= totalBudget ? 'text-green-600' : 'text-red-600'}
          icon={totalRealise >= totalBudget ? TrendingUp : TrendingDown}
        />
        <KpiCard
          label="Écart"
          value={fmt(totalEcart)}
          sub={totalEcart >= 0 ? 'Excédent' : 'Déficit'}
          color={totalEcart >= 0 ? 'text-green-600' : 'text-red-600'}
        />
      </div>

      {/* Tableau prévision/réalisé/écart par catégorie */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-primary/5 border-b border-primary/10">
          <h3 className="font-semibold text-gray-900 text-sm">
            Prévision vs Réalisé — {MONTH_LABELS[month - 1]} {year}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Catégorie</th>
                <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs">Type</th>
                <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs">Budget prévu</th>
                <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs">Réalisé</th>
                <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs">Écart</th>
                <th className="px-4 py-2 text-gray-500 font-medium text-xs">Avancement</th>
              </tr>
            </thead>
            <tbody>
              {data.categories.map((cat) => {
                const ecart = (cat.realise || 0) - (cat.budget || 0);
                const pct =
                  cat.budget > 0
                    ? Math.min(100, Math.round(((cat.realise || 0) / cat.budget) * 100))
                    : 0;
                const isRecette = cat.type === 'income';
                return (
                  <tr key={cat.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium text-gray-800">{cat.name}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          isRecette
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {isRecette ? 'Recette' : 'Dépense'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{fmt(cat.budget || 0)}</td>
                    <td
                      className={`px-4 py-2.5 text-right font-medium ${
                        isRecette ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {fmt(cat.realise || 0)}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-right font-medium ${
                        ecart >= 0 ? 'text-green-600' : 'text-red-500'
                      }`}
                    >
                      {ecart >= 0 ? '+' : ''}
                      {fmt(ecart)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                          <div
                            className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-green-500' : 'bg-primary'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400 w-8 text-right">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {/* Total row */}
              <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200 text-sm">
                <td className="px-4 py-2.5 text-gray-700" colSpan={2}>
                  Total
                </td>
                <td className="px-4 py-2.5 text-right text-gray-900">{fmt(totalBudget)}</td>
                <td className="px-4 py-2.5 text-right text-gray-900">{fmt(totalRealise)}</td>
                <td
                  className={`px-4 py-2.5 text-right ${
                    totalEcart >= 0 ? 'text-green-600' : 'text-red-500'
                  }`}
                >
                  {totalEcart >= 0 ? '+' : ''}
                  {fmt(totalEcart)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Onglet Flux bancaire ─────────────────────────────────────────────────────

// ─── Badge statut transaction ──────────────────────────────────────────────────
const STATUS_CONFIG = {
  to_validate: { label: 'A valider', icon: Clock, cls: 'bg-amber-100 text-amber-700' },
  validated:   { label: 'Valide',    icon: CheckCircle, cls: 'bg-green-100 text-green-700' },
  reconciled:  { label: 'Rapproche', icon: Link, cls: 'bg-blue-100 text-blue-700' },
};
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.to_validate;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      <Icon size={10} />
      {cfg.label}
    </span>
  );
};

// ─── Modal de rapprochement ────────────────────────────────────────────────────
const MatchingModal = ({ transaction, onClose, onMatched }) => {
  const toast = useToast();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);

  useEffect(() => {
    getTreasuryMatchingSuggestions(transaction.id)
      .then(r => setSuggestions(r.data || []))
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  }, [transaction.id]);

  const handleConfirm = async (pendingInvoiceId) => {
    setConfirming(pendingInvoiceId);
    try {
      await confirmTreasuryMatch({ transactionId: transaction.id, pendingInvoiceId });
      toast.success('Rapprochement effectue');
      onMatched();
      onClose();
    } catch {
      toast.error('Erreur rapprochement');
    } finally {
      setConfirming(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Rapprocher une facture</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {/* Transaction sélectionnée */}
        <div className="px-5 py-3 bg-primary/5 border-b border-primary/10">
          <p className="text-xs text-gray-500 mb-0.5">Transaction bancaire</p>
          <p className="text-sm font-semibold text-gray-800 truncate">{transaction.label}</p>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-gray-500">{fmtDate(transaction.date)}</span>
            <span className={`text-sm font-bold ${isIncome(transaction.type) ? 'text-green-600' : 'text-red-600'}`}>
              {isIncome(transaction.type) ? '+' : '-'}{fmt(Math.abs(transaction.amount))}
            </span>
          </div>
        </div>
        {/* Suggestions */}
        <div className="p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Factures fournisseurs correspondantes (±5%, ±30j)
          </p>
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-6">Recherche en cours...</p>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              <AlertCircle size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Aucune facture correspondante trouvée</p>
              <p className="text-xs mt-1">Importez des factures via N8N ou manuellement dans l'onglet Factures</p>
            </div>
          ) : (
            <div className="space-y-2">
              {suggestions.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {inv.vendor || inv.label || inv.fileName}
                    </p>
                    <p className="text-xs text-gray-500">{inv.invoiceDate ? fmtDate(inv.invoiceDate) : '—'}</p>
                  </div>
                  <div className="flex items-center gap-3 ml-3">
                    <span className="text-sm font-bold text-gray-800">{fmt(inv.amount)}</span>
                    <button
                      onClick={() => handleConfirm(inv.id)}
                      disabled={confirming === inv.id}
                      className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white text-xs rounded-lg hover:bg-primary-dark disabled:opacity-50"
                    >
                      <Link size={12} />
                      {confirming === inv.id ? '...' : 'Lier'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Onglet Flux bancaire ─────────────────────────────────────────────────────

const BankTab = ({ structureId, categories }) => {
  const toast = useToast();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showPdfImport, setShowPdfImport] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editTx, setEditTx] = useState(null);
  const [filterBank, setFilterBank] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [batches, setBatches] = useState([]);
  const [showBatches, setShowBatches] = useState(false);
  const [matchingTx, setMatchingTx] = useState(null);
  const [validating, setValidating] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchCategoryId, setBatchCategoryId] = useState('');
  const [batchCreateRule, setBatchCreateRule] = useState(true);
  const [batchCategorizing, setBatchCategorizing] = useState(false);

  const fetchTransactions = useCallback(async () => {
    if (!structureId) return;
    setLoading(true);
    setError(null);
    try {
      const params = { structureId };
      if (filterType) params.type = filterType;
      const res = await getTreasuryTransactions(params);
      setTransactions(res.data || []);
    } catch (e) {
      setError('Erreur de chargement des transactions');
      console.error(e);
      toast.error('Erreur de chargement des transactions');
    } finally {
      setLoading(false);
    }
  }, [structureId, filterType]);

  const fetchBatches = useCallback(async () => {
    try {
      const res = await getTreasuryImportBatches(structureId);
      setBatches(res.data || []);
    } catch {
      setBatches([]);
    }
  }, [structureId]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleValidate = async (id) => {
    setValidating(id);
    try {
      const res = await validateTreasuryTransaction(id);
      setTransactions(prev => prev.map(t => t.id === id ? { ...t, ...res.data } : t));
      toast.success('Transaction validee');
    } catch {
      toast.error('Erreur validation');
    } finally {
      setValidating(null);
    }
  };

  const handleBatchValidate = async () => {
    const toValidateIds = filtered.filter(t => t.status === 'to_validate').map(t => t.id);
    if (!toValidateIds.length) return;
    try {
      await batchValidateTreasuryTransactions(toValidateIds);
      toast.success(`${toValidateIds.length} transaction(s) validee(s)`);
      fetchTransactions();
    } catch {
      toast.error('Erreur validation en masse');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(t => t.id)));
    }
  };

  const handleBatchCategorize = async () => {
    if (!selectedIds.size || !batchCategoryId) return;
    setBatchCategorizing(true);
    try {
      const res = await batchCategorizeTreasuryTransactions({
        ids: [...selectedIds],
        categoryId: Number(batchCategoryId),
        createRule: batchCreateRule,
      });
      toast.success(`${res.data.updated} transaction(s) categorisee(s)`);
      setSelectedIds(new Set());
      setBatchCategoryId('');
      fetchTransactions();
    } catch {
      toast.error('Erreur categorisation en masse');
    } finally {
      setBatchCategorizing(false);
    }
  };

  const handleUnmatch = async (id) => {
    try {
      const res = await unmatchTreasuryTransaction(id);
      setTransactions(prev => prev.map(t => t.id === id ? { ...t, ...res.data } : t));
      toast.success('Rapprochement retire');
    } catch {
      toast.error('Erreur suppression rapprochement');
    }
  };

  const handleExportISuite = async () => {
    try {
      const res = await exportTreasuryISuite({ structureId });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `isuite_tresorerie.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export iSuite telecharge');
    } catch {
      toast.error('Erreur export iSuite');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette transaction ?')) return;
    try {
      await deleteTreasuryTransaction(id);
      toast.success('Transaction supprimee');
      fetchTransactions();
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la suppression de la transaction');
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm("Supprimer ce lot d'import et toutes ses transactions ?")) return;
    try {
      await deleteTreasuryImportBatch(batchId);
      toast.success('Lot supprime');
      fetchBatches();
      fetchTransactions();
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la suppression du lot');
    }
  };

  const TYPE_BADGE = {
    income: 'bg-green-100 text-green-700',
    recette: 'bg-green-100 text-green-700',
    expense: 'bg-red-100 text-red-700',
    depense: 'bg-red-100 text-red-700',
    virement: 'bg-gray-100 text-gray-700',
  };

  const TYPE_LABELS = { income: 'Recette', recette: 'Recette', expense: 'Dépense', depense: 'Dépense', virement: 'Virement' };

  // Banques disponibles pour le filtre (avant filtrage)
  const banks = [...new Set(transactions.map((t) => t.bankAccount).filter(Boolean))];

  // Filtres côté frontend
  const searchLow = search.toLowerCase();
  const filtered = transactions
    .filter(t => !filterBank || t.bankAccount === filterBank)
    .filter(t => !filterStatus || t.status === filterStatus)
    .filter(t => !searchLow || [
      t.label, t.notes, t.bankAccount,
      t.category?.name,
      t.pendingInvoices?.[0]?.vendor, t.pendingInvoices?.[0]?.label,
      String(Math.abs(t.amount || 0)),
      String(Math.abs(t.amount || 0)).replace('.', ','),
    ].some(v => v && v.toLowerCase().includes(searchLow)));

  const toValidateCount = transactions.filter(t => t.status === 'to_validate').length;

  // Totaux (sur les transactions filtrées)
  const totalRecettes = filtered
    .filter((t) => isIncome(t.type))
    .reduce((s, t) => s + Math.abs(t.amount || 0), 0);
  const totalDepenses = filtered
    .filter((t) => !isIncome(t.type))
    .reduce((s, t) => s + Math.abs(t.amount || 0), 0);
  const solde = totalRecettes - totalDepenses;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <KpiCard label="Recettes" value={fmt(totalRecettes)} color="text-green-600" icon={TrendingUp} />
        <KpiCard label="Dépenses" value={fmt(totalDepenses)} color="text-red-600" icon={TrendingDown} />
        <KpiCard
          label="Solde net"
          value={fmt(solde)}
          color={solde >= 0 ? 'text-primary' : 'text-red-600'}
          icon={Banknote}
        />
      </div>

      {/* Barre d'actions */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Recherche */}
        <div className="relative">
          <input
            type="text"
            placeholder="Rechercher..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="border border-gray-200 rounded-lg pl-7 pr-3 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 w-44"
          />
          <Filter size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={11} />
            </button>
          )}
        </div>
        {/* Filtres */}
        <div className="flex items-center gap-1 text-sm text-gray-500">
          <Filter size={14} />
        </div>
        <select
          className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 bg-white focus:outline-none"
          value={filterBank}
          onChange={(e) => setFilterBank(e.target.value)}
        >
          <option value="">Toutes les banques</option>
          {banks.map((b) => (
            <option key={b} value={b}>
              {BANK_LABELS[b] || b}
            </option>
          ))}
        </select>
        <select
          className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 bg-white focus:outline-none"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="">Tous les types</option>
          <option value="income">Recettes</option>
          <option value="expense">Dépenses</option>
          <option value="virement">Virements</option>
        </select>
        <select
          className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 bg-white focus:outline-none"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">Tous les statuts</option>
          <option value="to_validate">A valider{toValidateCount > 0 ? ` (${toValidateCount})` : ''}</option>
          <option value="validated">Valide</option>
          <option value="reconciled">Rapproche</option>
        </select>
        <button
          onClick={fetchTransactions}
          className="p-1.5 text-gray-400 hover:text-gray-600 border border-gray-200 rounded-lg"
        >
          <RefreshCw size={14} />
        </button>

        {/* Spacer */}
        <span className="flex-1" />

        {/* Tout valider */}
        {toValidateCount > 0 && (
          <button
            onClick={handleBatchValidate}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-green-300 text-green-700 rounded-lg hover:bg-green-50"
          >
            <CheckCircle size={13} />
            Tout valider ({toValidateCount})
          </button>
        )}

        {/* Export iSuite */}
        <button
          onClick={handleExportISuite}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
        >
          <Download size={13} />
          Export iSuite
        </button>

        {/* Gérer imports */}
        <button
          onClick={() => {
            setShowBatches(!showBatches);
            if (!showBatches) fetchBatches();
          }}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
        >
          <Trash2 size={13} />
          Gérer imports
        </button>

        {/* Nouvelle transaction */}
        <button
          onClick={() => {
            setEditTx(null);
            setShowForm(true);
          }}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
        >
          <Plus size={13} />
          Manuel
        </button>

        {/* Import CSV */}
        <button
          onClick={() => setShowImport(true)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
        >
          <Upload size={13} />
          Import CSV
        </button>

        {/* Import PDF (OCR LLM) */}
        <button
          onClick={() => setShowPdfImport(true)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
        >
          <FileText size={13} />
          Import PDF
        </button>
      </div>

      {/* Gestion des lots d'import */}
      {showBatches && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Lots d'import</h3>
          {batches.length === 0 ? (
            <p className="text-sm text-gray-400">Aucun lot d'import enregistré.</p>
          ) : (
            <div className="space-y-1">
              {batches.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm"
                >
                  <div className="flex flex-col">
                    <span className="text-gray-700 text-xs font-medium">{b.fileName || `Import #${b.id}`}</span>
                    <span className="text-gray-400 text-xs">{BANK_LABELS[b.bank] || b.bank} — {fmtDate(b.importedAt)}</span>
                  </div>
                  <span className="text-gray-500 text-xs">
                    {b.transactionCount} transaction{b.transactionCount > 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={() => handleDeleteBatch(b.id)}
                    className="text-red-400 hover:text-red-600 ml-3"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {/* Barre catégorisation en masse */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-primary">
            {selectedIds.size} transaction{selectedIds.size > 1 ? 's' : ''} sélectionnée{selectedIds.size > 1 ? 's' : ''}
          </span>
          <select
            value={batchCategoryId}
            onChange={e => setBatchCategoryId(e.target.value)}
            className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">Choisir une catégorie...</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={batchCreateRule}
              onChange={e => setBatchCreateRule(e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary/30"
            />
            Créer règle auto
          </label>
          <button
            onClick={handleBatchCategorize}
            disabled={!batchCategoryId || batchCategorizing}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition disabled:opacity-50"
          >
            <Tag size={13} />
            {batchCategorizing ? 'En cours...' : 'Appliquer'}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-gray-500 hover:text-gray-700 ml-auto"
          >
            Désélectionner tout
          </button>
        </div>
      )}

      {/* Tableau transactions */}
      {loading ? (
        <p className="text-sm text-gray-400 text-center py-10">Chargement...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Banknote size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">Aucune transaction</p>
          <p className="text-sm mt-1">Importez un relevé CSV ou ajoutez une transaction manuellement.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-2 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filtered.length && filtered.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-primary focus:ring-primary/30"
                    />
                  </th>
                  <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Date</th>
                  <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Libellé</th>
                  <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Catégorie</th>
                  <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Statut</th>
                  <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs">Montant</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((tx) => {
                  const cat = tx.category || categories.find((c) => c.id === tx.categoryId);
                  const rowBg = tx.status === 'to_validate' ? 'bg-amber-50/40' : '';
                  return (
                    <tr key={tx.id} className={`border-b border-gray-50 hover:bg-gray-50 ${rowBg} ${selectedIds.has(tx.id) ? 'bg-primary/5' : ''}`}>
                      <td className="px-2 py-2.5">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(tx.id)}
                          onChange={() => toggleSelect(tx.id)}
                          className="rounded border-gray-300 text-primary focus:ring-primary/30"
                        />
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap text-xs">
                        {fmtDate(tx.date)}
                      </td>
                      <td className="px-3 py-2.5">
                        <p className="text-gray-800 text-sm truncate max-w-[180px]" title={tx.label}>{tx.label}</p>
                        <p className="text-gray-400 text-xs">{BANK_LABELS[tx.bankAccount] || tx.bankAccount || ''}</p>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{cat?.name || '—'}</td>
                      <td className="px-4 py-2.5">
                        <StatusBadge status={tx.status} />
                        {tx.status === 'reconciled' && tx.pendingInvoices?.[0] && (
                          <p className="text-xs text-blue-500 mt-0.5 truncate max-w-[80px]" title={tx.pendingInvoices[0].vendor || tx.pendingInvoices[0].label}>
                            {tx.pendingInvoices[0].vendor || tx.pendingInvoices[0].label || 'Facture liée'}
                          </p>
                        )}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right font-medium whitespace-nowrap ${
                          isIncome(tx.type) ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {isIncome(tx.type) ? '+' : '-'}
                        {fmt(Math.abs(tx.amount || 0))}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-0.5">
                          {/* Valider */}
                          {tx.status === 'to_validate' && (
                            <button
                              onClick={() => handleValidate(tx.id)}
                              disabled={validating === tx.id}
                              className="p-1 text-green-500 hover:text-green-700 rounded"
                              title="Valider"
                            >
                              <Check size={13} />
                            </button>
                          )}
                          {/* Rapprocher / Délier */}
                          {tx.status !== 'reconciled' ? (
                            <button
                              onClick={() => setMatchingTx(tx)}
                              className="p-1 text-blue-400 hover:text-blue-600 rounded"
                              title="Rapprocher une facture"
                            >
                              <Link size={13} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleUnmatch(tx.id)}
                              className="p-1 text-blue-400 hover:text-red-500 rounded"
                              title="Supprimer le rapprochement"
                            >
                              <Unlink size={13} />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setEditTx(tx);
                              setShowForm(true);
                            }}
                            className="p-1 text-gray-400 hover:text-primary rounded"
                          >
                            <Edit size={13} />
                          </button>
                          <button
                            onClick={() => handleDelete(tx.id)}
                            className="p-1 text-gray-400 hover:text-red-500 rounded"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {showImport && (
        <ImportCsvModal
          categories={categories}
          structureId={structureId}
          onClose={() => setShowImport(false)}
          onSuccess={fetchTransactions}
        />
      )}
      {showPdfImport && (
        <ImportPdfModal
          categories={categories}
          structureId={structureId}
          onClose={() => setShowPdfImport(false)}
          onSuccess={fetchTransactions}
        />
      )}
      {showForm && (
        <TransactionModal
          transaction={editTx}
          categories={categories}
          structureId={structureId}
          onClose={() => {
            setShowForm(false);
            setEditTx(null);
          }}
          onSuccess={fetchTransactions}
        />
      )}
      {matchingTx && (
        <MatchingModal
          transaction={matchingTx}
          onClose={() => setMatchingTx(null)}
          onMatched={() => {
            setMatchingTx(null);
            fetchTransactions();
          }}
        />
      )}
    </div>
  );
};

// ─── Onglet Vue annuelle ──────────────────────────────────────────────────────

const AnnualTab = ({ structureId, exerciceYear, setExerciceYear }) => {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!structureId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getTreasuryDashboard({
        structureId,
        view: 'annual',
        exerciceYear,
      });
      setData(res.data);
    } catch (e) {
      setError('Erreur de chargement de la vue annuelle');
      console.error(e);
      toast.error('Erreur de chargement de la vue annuelle');
    } finally {
      setLoading(false);
    }
  }, [structureId, exerciceYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exerciceMonths = getExerciceMonths(exerciceYear);

  // Indexer les données par mois
  const byMonth = {};
  if (data?.months) {
    data.months.forEach((m) => {
      byMonth[m.monthKey] = m;
    });
  }

  const totals = exerciceMonths.reduce(
    (acc, { monthKey }) => {
      const m = byMonth[monthKey];
      return {
        recettes: acc.recettes + (m?.recettes || 0),
        depenses: acc.depenses + (m?.depenses || 0),
      };
    },
    { recettes: 0, depenses: 0 }
  );

  return (
    <div className="space-y-4">
      {/* Sélecteur exercice */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-600">Exercice :</span>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1">
          <button
            onClick={() => setExerciceYear((y) => y - 1)}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm font-semibold text-gray-900 px-2">
            {fmtExercice(exerciceYear)}
          </span>
          <button
            onClick={() => setExerciceYear((y) => y + 1)}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <button onClick={fetchData} className="p-1.5 text-gray-400 hover:text-gray-600 border border-gray-200 rounded-lg">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* KPIs exercice */}
      {!loading && data && (
        <div className="grid grid-cols-3 gap-3">
          <KpiCard label="Recettes exercice" value={fmt(totals.recettes)} color="text-green-600" icon={TrendingUp} />
          <KpiCard label="Dépenses exercice" value={fmt(totals.depenses)} color="text-red-600" icon={TrendingDown} />
          <KpiCard
            label="Résultat exercice"
            value={fmt(totals.recettes - totals.depenses)}
            color={totals.recettes - totals.depenses >= 0 ? 'text-primary' : 'text-red-600'}
            icon={BarChart3}
          />
        </div>
      )}

      {/* Tableau Sep → Août */}
      {loading ? (
        <p className="text-sm text-gray-400 text-center py-10">Chargement...</p>
      ) : error ? (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-primary/5 border-b border-primary/10">
            <h3 className="font-semibold text-gray-900 text-sm">{fmtExercice(exerciceYear)} — Sep → Août</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Mois</th>
                  <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs text-green-600">Recettes</th>
                  <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs text-red-500">Dépenses</th>
                  <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs">Résultat mois</th>
                  <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs text-primary">Solde fin de mois</th>
                  <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs">Nb tx</th>
                </tr>
              </thead>
              <tbody>
                {exerciceMonths.map(({ monthKey, monthLabel }, idx) => {
                  const m = byMonth[monthKey];
                  const resultat = (m?.recettes || 0) - (m?.depenses || 0);
                  const prevKey = idx > 0 ? exerciceMonths[idx - 1].monthKey : null;
                  const prevSolde = prevKey ? byMonth[prevKey]?.solde ?? null : null;
                  const solde = m?.solde ?? null;
                  const trend = solde !== null && prevSolde !== null
                    ? solde > prevSolde ? '↑' : solde < prevSolde ? '↓' : '='
                    : null;
                  const trendColor = trend === '↑' ? 'text-green-500' : trend === '↓' ? 'text-red-400' : 'text-gray-400';

                  if (!m)
                    return (
                      <tr key={monthKey} className="border-b border-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-400 text-xs">{monthLabel}</td>
                        <td colSpan={5} className="text-center text-gray-200 text-xs">—</td>
                      </tr>
                    );
                  return (
                    <tr key={monthKey} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-semibold text-gray-700 text-xs">{monthLabel}</td>
                      <td className="px-4 py-2.5 text-right text-green-600 font-medium text-xs">{fmt(m.recettes || 0)}</td>
                      <td className="px-4 py-2.5 text-right text-red-500 text-xs">{fmt(m.depenses || 0)}</td>
                      <td className={`px-4 py-2.5 text-right font-medium text-xs ${resultat >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {resultat >= 0 ? '+' : ''}{fmt(resultat)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-xs">
                        {solde !== null ? (
                          <span className={`font-bold ${solde >= 0 ? 'text-primary' : 'text-red-600'} ${m.soldeProjected ? 'opacity-50' : ''}`}>
                            {fmt(solde)}
                            {m.soldeProjected && <span className="ml-1 text-gray-400 font-normal text-[10px]">proj.</span>}
                            {trend && <span className={`ml-1 ${trendColor}`}>{trend}</span>}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-400 text-xs">{m.count || 0}</td>
                    </tr>
                  );
                })}
                {/* Total */}
                <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200 text-sm">
                  <td className="px-4 py-2.5 text-gray-700 text-xs">Total exercice</td>
                  <td className="px-4 py-2.5 text-right text-green-600 text-xs">{fmt(totals.recettes)}</td>
                  <td className="px-4 py-2.5 text-right text-red-500 text-xs">{fmt(totals.depenses)}</td>
                  <td className={`px-4 py-2.5 text-right text-xs ${totals.recettes - totals.depenses >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {totals.recettes - totals.depenses >= 0 ? '+' : ''}{fmt(totals.recettes - totals.depenses)}
                  </td>
                  <td />
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Onglet Budget ────────────────────────────────────────────────────────────

const BudgetTab = ({ structureId, month, year }) => {
  const toast = useToast();
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState({}); // { categoryId: amount }
  const [editValues, setEditValues] = useState({}); // { categoryId: string }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // categoryId en cours de sauvegarde
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!structureId) return;
    setLoading(true);
    setError(null);
    try {
      const [catRes, budRes] = await Promise.all([
        getTreasuryCategories({ structureId }),
        getTreasuryBudgets({ structureId, month, year }),
      ]);
      const cats = catRes.data || [];
      const buds = budRes.data || [];
      setCategories(cats);
      const budMap = {};
      const editMap = {};
      buds.forEach((b) => {
        budMap[b.categoryId] = b.amount;
        editMap[b.categoryId] = String(b.amount ?? '');
      });
      cats.forEach((c) => {
        if (!(c.id in editMap)) {
          editMap[c.id] = '';
        }
      });
      setBudgets(budMap);
      setEditValues(editMap);
    } catch (e) {
      setError('Erreur de chargement des budgets');
      console.error(e);
      toast.error('Erreur de chargement des budgets');
    } finally {
      setLoading(false);
    }
  }, [structureId, month, year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async (categoryId) => {
    const raw = editValues[categoryId];
    const amount = raw !== '' ? parseFloat(raw) : null;
    if (raw !== '' && isNaN(amount)) return;
    setSaving(categoryId);
    try {
      await upsertTreasuryBudget({ structureId, categoryId, month, year, amount });
      setBudgets((prev) => ({ ...prev, [categoryId]: amount }));
      toast.success('Budget enregistre');
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la sauvegarde du budget');
    } finally {
      setSaving(null);
    }
  };

  const totalRecettes = categories
    .filter((c) => isIncome(c.type))
    .reduce((s, c) => s + (budgets[c.id] || 0), 0);
  const totalDepenses = categories
    .filter((c) => !isIncome(c.type))
    .reduce((s, c) => s + (budgets[c.id] || 0), 0);

  const recettesCats = categories.filter((c) => isIncome(c.type));
  const depensesCats = categories.filter((c) => !isIncome(c.type));

  const renderGroup = (cats, label, colorCls) => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className={`px-4 py-3 border-b border-gray-100 flex items-center justify-between`}>
        <h3 className={`font-semibold text-sm ${colorCls}`}>{label}</h3>
        <span className={`text-sm font-bold ${colorCls}`}>
          {fmt(label === 'Recettes' ? totalRecettes : totalDepenses)}
        </span>
      </div>
      <div className="divide-y divide-gray-50">
        {cats.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-400">Aucune catégorie</p>
        ) : (
          cats.map((cat) => (
            <div key={cat.id} className="flex items-center gap-3 px-4 py-3">
              <span className="flex-1 text-sm text-gray-700 font-medium">{cat.name}</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-32 text-right focus:outline-none focus:ring-2 focus:ring-primary/30"
                  value={editValues[cat.id] ?? ''}
                  onChange={(e) =>
                    setEditValues((prev) => ({ ...prev, [cat.id]: e.target.value }))
                  }
                  onBlur={() => handleSave(cat.id)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSave(cat.id)}
                />
                <span className="text-xs text-gray-400">€</span>
                {saving === cat.id ? (
                  <RefreshCw size={13} className="text-primary animate-spin" />
                ) : budgets[cat.id] != null ? (
                  <Check size={13} className="text-green-500" />
                ) : (
                  <span className="w-3.5" />
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Saisissez les montants prévisionnels pour{' '}
          <strong>
            {MONTH_LABELS[month - 1]} {year}
          </strong>
          . Les modifications sont sauvegardées automatiquement.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 text-center py-10">Chargement...</p>
      ) : error ? (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {renderGroup(recettesCats, 'Recettes', 'text-green-600')}
          {renderGroup(depensesCats, 'Dépenses', 'text-red-600')}
        </div>
      )}

      {!loading && !error && (
        <div className="bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Budget net prévisionnel</span>
          <span
            className={`text-sm font-bold ${
              totalRecettes - totalDepenses >= 0 ? 'text-primary' : 'text-red-600'
            }`}
          >
            {totalRecettes - totalDepenses >= 0 ? '+' : ''}
            {fmt(totalRecettes - totalDepenses)}
          </span>
        </div>
      )}
    </div>
  );
};

// ─── Onglet Factures fournisseurs (Sprint 3) ──────────────────────────────────

const STATUS_INVOICE = {
  pending:  { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Validée',    cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejetée',    cls: 'bg-red-100 text-red-700' },
};

const InvoicesTab = ({ structureId, categories }) => {
  const toast = useToast();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [showUpload, setShowUpload] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [linkTxId, setLinkTxId] = useState('');
  const [txForLink, setTxForLink] = useState([]);
  // Rapprochement depuis la facture
  const [matchInvoice, setMatchInvoice] = useState(null);
  const [matchingTxs, setMatchingTxs] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [selectedMatchTxId, setSelectedMatchTxId] = useState('');

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const res = await getTreasuryPendingInvoices(params);
      setInvoices(res.data || []);
    } catch {
      toast.error('Erreur chargement factures');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger les transactions pour liaison
  useEffect(() => {
    if (!structureId) return;
    getTreasuryTransactions({ structureId })
      .then(r => setTxForLink((r.data || []).filter(t => t.status !== 'reconciled')))
      .catch(() => {});
  }, [structureId]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleReject = async (id) => {
    try {
      await rejectTreasuryInvoice(id);
      toast.success('Facture rejetee');
      fetchInvoices();
    } catch {
      toast.error('Erreur rejet');
    }
  };

  const handleApprove = async (invoice) => {
    try {
      await approveTreasuryInvoice(invoice.id, {
        transactionId: linkTxId ? Number(linkTxId) : undefined,
      });
      toast.success('Facture validee' + (linkTxId ? ' et liee au releve' : ''));
      setEditInvoice(null);
      setLinkTxId('');
      fetchInvoices();
    } catch {
      toast.error('Erreur validation');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await deleteTreasuryInvoice(id);
      toast.success('Facture supprimee');
      fetchInvoices();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const handleOpenMatch = async (inv) => {
    setMatchInvoice(inv);
    setSelectedMatchTxId('');
    setMatchingTxs([]);
    setMatchLoading(true);
    try {
      const res = await getTreasuryInvoiceMatchingTransactions(inv.id);
      setMatchingTxs(res.data || []);
    } catch {
      toast.error('Erreur chargement transactions');
    } finally {
      setMatchLoading(false);
    }
  };

  const handleConfirmMatch = async () => {
    if (!matchInvoice) return;
    try {
      await approveTreasuryInvoice(matchInvoice.id, {
        transactionId: selectedMatchTxId ? Number(selectedMatchTxId) : undefined,
      });
      toast.success('Facture validee' + (selectedMatchTxId ? ' et liee au releve' : ''));
      setMatchInvoice(null);
      fetchInvoices();
    } catch {
      toast.error('Erreur validation');
    }
  };

  const pendingCount = invoices.filter(i => i.status === 'pending').length;

  return (
    <div className="space-y-4">
      {/* Barre d'actions */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 bg-white focus:outline-none"
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="">Toutes</option>
          <option value="pending">En attente{pendingCount > 0 ? ` (${pendingCount})` : ''}</option>
          <option value="approved">Validees</option>
          <option value="rejected">Rejetees</option>
        </select>
        <button
          onClick={fetchInvoices}
          className="p-1.5 text-gray-400 hover:text-gray-600 border border-gray-200 rounded-lg"
        >
          <RefreshCw size={14} />
        </button>
        <span className="flex-1" />
        <button
          onClick={() => setShowUpload(v => !v)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark"
        >
          <Plus size={13} />
          Ajouter une facture
        </button>
      </div>

      {/* Upload manuel */}
      {showUpload && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Ajouter une facture manuellement</h3>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              try {
                const fd = new FormData();
                fd.append('file', file);
                if (structureId && structureId !== 'common') fd.append('structureId', structureId);
                await uploadTreasuryInvoice(fd);
                toast.success('Facture ajoutee — extraction en cours...');
                setShowUpload(false);
                setTimeout(fetchInvoices, 1500);
              } catch {
                toast.error('Erreur upload');
              }
            }}
          />
          <p className="text-xs text-gray-400 mt-2">PDF ou image (max 20 Mo). L'extraction LLM est automatique.</p>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <p className="text-sm text-gray-400 text-center py-10">Chargement...</p>
      ) : invoices.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileCheck size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">Aucune facture</p>
          <p className="text-sm mt-1">Les factures recues via N8N ou ajoutees manuellement apparaissent ici.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Fichier / Libellé</th>
                  <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Fournisseur</th>
                  <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Date facture</th>
                  <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Statut</th>
                  <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs">Montant</th>
                  <th className="text-center px-4 py-2 text-gray-500 font-medium text-xs">Source</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const st = STATUS_INVOICE[inv.status] || STATUS_INVOICE.pending;
                  return (
                    <tr key={inv.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-2.5">
                        <a
                          href={inv.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          <FileText size={12} />
                          <span className="truncate max-w-[140px]">{inv.fileName}</span>
                        </a>
                        {inv.label && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[160px]">{inv.label}</p>}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700 text-xs">{inv.vendor || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{fmtDate(inv.invoiceDate)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>
                          {st.label}
                        </span>
                        {inv.transaction && (
                          <p className="text-xs text-blue-500 mt-0.5 truncate max-w-[120px]">
                            → {inv.transaction.label?.substring(0, 30)}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium text-gray-800 text-xs whitespace-nowrap">
                        {inv.amount ? fmt(inv.amount) : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${inv.source === 'n8n' ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-500'}`}>
                          {inv.source === 'n8n' ? 'N8N' : 'Manuel'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        {inv.status === 'pending' && (
                          <div className="flex items-center gap-0.5">
                            <button
                              onClick={() => handleOpenMatch(inv)}
                              className="p-1 text-blue-500 hover:text-blue-700 rounded"
                              title="Rapprocher avec le releve bancaire"
                            >
                              <Link size={13} />
                            </button>
                            <button
                              onClick={() => { setEditInvoice(inv); setLinkTxId(''); }}
                              className="p-1 text-green-500 hover:text-green-700 rounded"
                              title="Valider sans rapprochement"
                            >
                              <Check size={13} />
                            </button>
                            <button
                              onClick={() => handleReject(inv.id)}
                              className="p-1 text-red-400 hover:text-red-600 rounded"
                              title="Rejeter"
                            >
                              <X size={13} />
                            </button>
                          </div>
                        )}
                        {inv.status !== 'pending' && (
                          <button onClick={() => handleDelete(inv.id)} className="p-1 text-gray-300 hover:text-red-500 rounded">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal rapprochement depuis facture */}
      {matchInvoice && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Rapprocher avec le releve bancaire</h2>
              <button onClick={() => setMatchInvoice(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-blue-50 rounded-lg px-4 py-3 text-sm">
                <p className="font-medium text-gray-800">{matchInvoice.vendor || matchInvoice.fileName}</p>
                <p className="text-gray-500 text-xs mt-0.5">
                  {matchInvoice.amount ? fmt(matchInvoice.amount) : 'Montant inconnu'} — {fmtDate(matchInvoice.invoiceDate)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">
                  Transactions correspondantes du releve (&plusmn;5%, &plusmn;30j) :
                </p>
                {matchLoading ? (
                  <p className="text-sm text-gray-400 text-center py-4">Recherche en cours...</p>
                ) : matchingTxs.length === 0 ? (
                  <div className="text-center py-6 text-gray-400">
                    <AlertCircle size={28} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Aucune transaction correspondante trouvee dans le releve.</p>
                    <p className="text-xs mt-1">Verifiez que le montant et la date de la facture sont corrects.</p>
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {matchingTxs.map(tx => (
                      <label
                        key={tx.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedMatchTxId === String(tx.id)
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="matchTx"
                          value={tx.id}
                          checked={selectedMatchTxId === String(tx.id)}
                          onChange={() => setSelectedMatchTxId(String(tx.id))}
                          className="text-primary"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">{tx.label || '—'}</p>
                          <p className="text-xs text-gray-400">{fmtDate(tx.date)} — {tx.category?.name || 'Sans categorie'}</p>
                        </div>
                        <span className="text-xs font-semibold text-red-600 whitespace-nowrap">
                          {fmt(Math.abs(tx.amount))}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  Valider la facture lie egalement la transaction comme "Rapprochee".
                </p>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={() => setMatchInvoice(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmMatch}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center gap-1.5"
              >
                <Check size={14} />
                {selectedMatchTxId ? 'Valider et rapprocher' : 'Valider sans liaison'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal validation + liaison transaction */}
      {editInvoice && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Valider la facture</h2>
              <button onClick={() => setEditInvoice(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm">
                <p className="font-medium text-gray-800">{editInvoice.vendor || editInvoice.fileName}</p>
                <p className="text-gray-500 text-xs mt-0.5">
                  {editInvoice.amount ? fmt(editInvoice.amount) : 'Montant inconnu'} — {fmtDate(editInvoice.invoiceDate)}
                </p>
                {editInvoice.label && <p className="text-gray-400 text-xs mt-0.5">{editInvoice.label}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Lier à une transaction du relevé (optionnel)
                </label>
                <select
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  value={linkTxId}
                  onChange={e => setLinkTxId(e.target.value)}
                >
                  <option value="">— Valider sans liaison</option>
                  {txForLink.map(tx => (
                    <option key={tx.id} value={tx.id}>
                      {fmtDate(tx.date)} — {(tx.label || '').substring(0, 40)} — {fmt(Math.abs(tx.amount))}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={() => setEditInvoice(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={() => handleApprove(editInvoice)}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center gap-1.5"
              >
                <Check size={14} />
                Valider
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Page principale ───────────────────────────────────────────────────────────

const TreasuryPage = () => {
  const toast = useToast();
  const { currentStructure, structures } = useStructure();

  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard | monthly | bank | annual | budget
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Exercice fiscal pour la vue annuelle : Sep de l'année précédente si on est avant septembre
  const currentMonth = new Date().getMonth() + 1;
  const defaultExerciceYear = currentMonth >= 9 ? new Date().getFullYear() : new Date().getFullYear() - 1;
  const [exerciceYear, setExerciceYear] = useState(defaultExerciceYear);

  // Catégories globales (chargées une seule fois, partagées entre onglets)
  const [categories, setCategories] = useState([]);

  // Sélecteur propre à la Trésorerie (indépendant du contexte global)
  const [treasuryView, setTreasuryView] = useState('common'); // structureId ou 'common'
  const [consolidated, setConsolidated] = useState(null);

  // Charger le résumé consolidé (toutes structures + commun)
  useEffect(() => {
    if (!structures.length) return;
    const ids = structures.map(s => s.id).join(',');
    getTreasuryConsolidated({ structureIds: ids })
      .then(r => setConsolidated(r.data))
      .catch(() => {});
  }, [structures]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getTreasuryCategories({ structureId: treasuryView });
      setCategories(res.data || []);
    } catch (e) {
      console.error('Erreur chargement catégories trésorerie', e);
      toast.error('Erreur de chargement des categories');
    }
  }, [treasuryView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const TABS = [
    { id: 'dashboard', label: 'Tableau de bord', icon: TrendingUp },
    { id: 'monthly', label: 'Vue mensuelle', icon: Calendar },
    { id: 'bank', label: 'Flux bancaire', icon: Banknote },
    { id: 'annual', label: 'Vue annuelle', icon: BarChart3 },
    { id: 'budget', label: 'Budget', icon: FileText },
    { id: 'invoices', label: 'Factures', icon: FileCheck },
  ];

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <TrendingUp size={22} className="text-primary" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Trésorerie</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {treasuryView === 'common' ? 'Compte commun' : (structures.find(s => s.id === treasuryView)?.name || '')}
            </p>
          </div>
        </div>

        {/* Sélecteur mois/année (visible sur monthly et budget) */}
        {activeTab === 'dashboard' && (
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1">
            <button onClick={() => setSelectedYear(y => y - 1)} className="p-1 text-gray-400 hover:text-gray-600"><ChevronLeft size={16} /></button>
            <span className="text-sm font-semibold text-gray-900 px-2">{selectedYear}</span>
            <button onClick={() => setSelectedYear(y => y + 1)} className="p-1 text-gray-400 hover:text-gray-600"><ChevronRight size={16} /></button>
          </div>
        )}
        {(activeTab === 'monthly' || activeTab === 'budget') && (
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1">
            <button
              onClick={() => {
                if (selectedMonth === 1) {
                  setSelectedMonth(12);
                  setSelectedYear((y) => y - 1);
                } else {
                  setSelectedMonth((m) => m - 1);
                }
              }}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-gray-900 px-2 whitespace-nowrap">
              {MONTH_LABELS[selectedMonth - 1]} {selectedYear}
            </span>
            <button
              onClick={() => {
                if (selectedMonth === 12) {
                  setSelectedMonth(1);
                  setSelectedYear((y) => y + 1);
                } else {
                  setSelectedMonth((m) => m + 1);
                }
              }}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Sélecteur de vue Trésorerie */}
      <div className="flex items-center gap-2 flex-wrap">
        {structures.map(s => (
          <button
            key={s.id}
            onClick={() => setTreasuryView(s.id)}
            className={`px-3 py-1.5 text-xs rounded-lg font-medium border transition ${
              treasuryView === s.id
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}
          >
            {s.name}
          </button>
        ))}
        <button
          onClick={() => setTreasuryView('common')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-medium border transition ${
            treasuryView === 'common'
              ? 'bg-amber-500 text-white border-amber-500'
              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
          }`}
        >
          <Building size={12} />
          Compte commun
        </button>
      </div>

      {/* Bandeau consolidé */}
      {consolidated && (
        <div className="grid grid-cols-3 gap-3">
          <KpiCard
            label="Recettes globales"
            value={fmt(consolidated.recettes)}
            color="text-green-600"
            icon={TrendingUp}
          />
          <KpiCard
            label="Dépenses globales"
            value={fmt(consolidated.depenses)}
            color="text-red-600"
            icon={TrendingDown}
          />
          <KpiCard
            label="Solde consolidé"
            value={fmt(consolidated.solde)}
            color={consolidated.solde >= 0 ? 'text-primary' : 'text-red-600'}
            icon={Banknote}
          />
        </div>
      )}

      {/* Onglets */}
      <div className="border-b border-gray-200">
        <div className="flex gap-0 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'dashboard' && (
        <DashboardTab structureId={treasuryView} year={selectedYear} />
      )}
      {activeTab === 'monthly' && (
        <MonthlyTab
          structureId={treasuryView}
          month={selectedMonth}
          year={selectedYear}
        />
      )}
      {activeTab === 'bank' && (
        <BankTab structureId={treasuryView} categories={categories} />
      )}
      {activeTab === 'annual' && (
        <AnnualTab
          structureId={treasuryView}
          exerciceYear={exerciceYear}
          setExerciceYear={setExerciceYear}
        />
      )}
      {activeTab === 'budget' && (
        <BudgetTab
          structureId={treasuryView}
          categories={categories}
          month={selectedMonth}
          year={selectedYear}
        />
      )}
      {activeTab === 'invoices' && (
        <InvoicesTab
          structureId={treasuryView}
          categories={categories}
        />
      )}
    </div>
  );
};

export default TreasuryPage;
